import { onScopeDispose, ref, watch, Ref } from 'vue'
import type { CommerceSettings } from '../services/MetaService/types'

export interface CatalogVisibilityApi {
  getCommerceSettings(wabaId: string, phoneId: string): Promise<CommerceSettings>
  setCatalogVisibility(wabaId: string, phoneId: string, visible: boolean): Promise<CommerceSettings>
}

export function useCatalogVisibility (
  wabaId: Ref<string>, phoneId: Ref<string>, getApi: () => Promise<CatalogVisibilityApi>
) {
  const visible = ref<boolean | null>()
  const loading = ref(false)
  const saving = ref(false)
  const error = ref('')
  let generation = 0
  let disposed = false

  async function refresh () {
    const request = ++generation
    const waba = wabaId.value
    const phone = phoneId.value
    visible.value = undefined
    error.value = ''
    saving.value = false
    loading.value = Boolean(waba && phone)
    if (!waba || !phone) return
    try {
      const result = await (await getApi()).getCommerceSettings(waba, phone)
      if (!disposed && request === generation) visible.value = result.is_catalog_visible
    } catch (e) {
      if (!disposed && request === generation) error.value = e instanceof Error ? e.message : String(e)
    } finally {
      if (!disposed && request === generation) loading.value = false
    }
  }

  async function save (value: boolean): Promise<boolean> {
    if (disposed || loading.value || saving.value || visible.value === undefined || !wabaId.value || !phoneId.value) return false
    const request = ++generation
    const waba = wabaId.value
    const phone = phoneId.value
    saving.value = true
    error.value = ''
    try {
      const result = await (await getApi()).setCatalogVisibility(waba, phone, value)
      if (result.is_catalog_visible !== value) throw new Error('Meta did not confirm the requested catalog visibility')
      if (disposed || request !== generation) return false
      visible.value = result.is_catalog_visible
      return true
    } catch (e) {
      if (!disposed && request === generation) {
        visible.value = undefined
        error.value = e instanceof Error ? e.message : String(e)
      }
      return false
    } finally {
      if (!disposed && request === generation) saving.value = false
    }
  }

  watch([wabaId, phoneId], refresh, { immediate: true, flush: 'sync' })
  onScopeDispose(() => { disposed = true; generation++ })
  return { visible, loading, saving, error, refresh, save }
}
